import { Link } from "react-router-dom";
import { AdvisorLogoIcon } from "@/components/icons/AdvisorLogo";

// ─────────────────────────────────────────────────────────────────────────────
// Generic privacy policy. Reachable publicly at /privacy (no auth) so it can be
// used as the OAuth consent-screen "Privacy policy URL" for Google verification.
//
// TEMPLATE NOTICE: this is a reasonable, honest starting point that matches what
// the app actually does, but it is NOT legal advice. Before you rely on it for a
// production launch, review these placeholders and have counsel look it over.
// ─────────────────────────────────────────────────────────────────────────────
const COMPANY_NAME = "ADVisor";
const CONTACT_EMAIL = "support@ad-visor.dev";
const EFFECTIVE_DATE = "July 25, 2026";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="text-lg font-bold text-neutral-900 mb-2">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-neutral-600">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="h-14 border-b border-neutral-200 bg-white flex items-center px-6">
        <Link to="/login" className="flex items-center gap-2.5">
          <AdvisorLogoIcon size="sm" className="text-steel-600" />
          <span className="font-bold text-sm text-steel-600 tracking-tight">{COMPANY_NAME}</span>
        </Link>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <h1 className="text-2xl font-bold text-neutral-900">Privacy Policy</h1>
        <p className="text-sm text-neutral-400 mt-1">Last updated: {EFFECTIVE_DATE}</p>

        <p className="mt-6 text-sm leading-relaxed text-neutral-600">
          This Privacy Policy explains how {COMPANY_NAME} (&ldquo;we&rdquo;, &ldquo;us&rdquo;, or
          &ldquo;our&rdquo;) collects, uses, and protects information when you use our application
          and related services (the &ldquo;Service&rdquo;). By using the Service, you agree to the
          practices described here.
        </p>

        <Section title="1. Information We Collect">
          <p>We collect the following categories of information:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              <strong>Account information.</strong> When you sign in with Google, we receive your
              name, email address, and profile picture as permitted by your Google account.
            </li>
            <li>
              <strong>Profile details.</strong> Information you provide during onboarding or in
              settings, such as job title, company name, team size, and notification preferences.
            </li>
            <li>
              <strong>Connected platform data.</strong> If you connect advertising or analytics
              accounts (such as Google Ads, Google Analytics, or Meta), we access campaign,
              performance, and reporting data from those accounts on your behalf to provide the
              Service.
            </li>
            <li>
              <strong>Usage data.</strong> Basic technical and product-analytics information about
              how you interact with the Service, used to operate and improve it.
            </li>
          </ul>
        </Section>

        <Section title="2. How We Use Information">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>To provide, maintain, and improve the Service and its features.</li>
            <li>To generate reports, insights, and recommendations you request.</li>
            <li>To authenticate you and secure your account.</li>
            <li>To communicate with you, including notifications you have opted into.</li>
            <li>To comply with legal obligations and enforce our terms.</li>
          </ul>
        </Section>

        <Section title="3. Google User Data and Limited Use">
          <p>
            Our use of information received from Google APIs adheres to the{" "}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-steel-600 underline"
            >
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements. Specifically:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>We only request the scopes necessary to provide features you use.</li>
            <li>
              We use Google user data solely to provide and improve user-facing features within the
              Service.
            </li>
            <li>
              We do not sell Google user data, and we do not use it for advertising.
            </li>
            <li>
              We do not transfer Google user data to third parties except as necessary to provide
              the Service, comply with applicable law, or as part of a merger or acquisition with
              appropriate protections.
            </li>
            <li>
              We do not allow humans to read Google user data unless we have your consent for
              specific data, it is necessary for security or to comply with law, or the data has
              been aggregated and anonymized.
            </li>
          </ul>
        </Section>

        <Section title="4. How We Share Information">
          <p>
            We do not sell your personal information. We may share information with trusted
            service providers (such as cloud hosting, authentication, and analytics providers) who
            process it on our behalf under appropriate confidentiality obligations, and with the
            advertising platforms you explicitly connect. We may also disclose information where
            required by law.
          </p>
        </Section>

        <Section title="5. Data Retention and Deletion">
          <p>
            We retain your information for as long as your account is active or as needed to provide
            the Service. You can delete your account at any time from Settings &rarr; Danger Zone.
            When you delete your account, we revoke connected platform tokens, remove your data from
            our systems, and delete your sign-in identity, subject to any retention required by law.
          </p>
        </Section>

        <Section title="6. Security">
          <p>
            We use industry-standard measures to protect your information, including encryption of
            access tokens and secure authentication. No method of transmission or storage is
            completely secure, and we cannot guarantee absolute security.
          </p>
        </Section>

        <Section title="7. Cookies">
          <p>
            We use cookies and similar technologies to keep you signed in and to understand product
            usage. You can control cookies through your browser settings, though some features may
            not function without them.
          </p>
        </Section>

        <Section title="8. Children's Privacy">
          <p>
            The Service is not directed to individuals under the age of 16, and we do not knowingly
            collect personal information from children.
          </p>
        </Section>

        <Section title="9. International Users">
          <p>
            Your information may be processed and stored in countries other than your own. Where
            required, we take steps to ensure appropriate safeguards are in place.
          </p>
        </Section>

        <Section title="10. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. When we do, we will revise the
            &ldquo;Last updated&rdquo; date above. Continued use of the Service after changes take
            effect constitutes acceptance of the updated policy.
          </p>
        </Section>

        <Section title="11. Contact Us">
          <p>
            If you have questions about this Privacy Policy or your data, contact us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-steel-600 underline">
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </Section>

        <footer className="mt-12 pt-6 border-t border-neutral-200 flex items-center justify-between text-xs text-neutral-400">
          <span>
            &copy; {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.
          </span>
          <Link to="/terms" className="hover:text-neutral-600 underline">
            Terms of Service
          </Link>
        </footer>
      </main>
    </div>
  );
}
